import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import i18n from 'i18next';

import CategoryNavigator from './categoryNavigator';
import useDebounce from '../hooks/useDebounce';
import { useUrlSearch } from '@/search';

import CustomFilterNotice from './customFilterNotice';

import styles from '../styles/filterableSearch.module.css';

const FilterableSearch = ({
  className = '',
  customFilterText = '',
  filters = [],
  hasCustomFilterButton = false,
  isChatOpen = false,
  isDisabled = false,
  onCustomFilterClick = () => {},
  onEditCustomFilterClick = () => {},
  onSelectChange = () => {},
  searchAriaLabel = '',
  searchPlaceholder = '',
  searchQueryParam = 'search',
  selectOptions = [],
  selectPlaceholder = i18n.t('select-an-option'),
  selectValue = null,
  shouldShowSelect = false,
}) => {
  const { search } = useLocation();
  const urlSearch = useUrlSearch();
  const [searchValue, setSearchValue] = useState(urlSearch.getValue(searchQueryParam) || '');
  const filterParams = filters.map((filter) => filter.key);

  const hasActiveFilters = () => {
    const searchParams = urlSearch.getParams(search);
    return filterParams.some((param) => searchParams.has(param));
  };

  const [shouldShowFilters, setShouldShowFilters] = useState(isChatOpen || hasActiveFilters());

  const selectedFilterValues = filters.reduce((a, { field_type: type, key }) => {
    const values = urlSearch.getValues(key).flatMap((value) => {
      if (!value) return [];

      return type === 'Integer' ? Number(value) : value;
    });

    return values.length ? { ...a, [key]: values } : a;
  }, {});

  const onCategoryNavigatorBlur = () => {
    if (isChatOpen || hasActiveFilters() || searchValue) return;

    setShouldShowFilters(false);
  };

  const updateSearchUrlImmediately = (updatedSearchValue) => {
    const params = urlSearch.getParams(search);
    if (updatedSearchValue === '') {
      params.delete(searchQueryParam);
    } else {
      params.set(searchQueryParam, updatedSearchValue);
    }
    params.delete('page');
    urlSearch.applyParams(params);
  };

  const handleUpdateSearchUrl = useDebounce(updateSearchUrlImmediately, 600);

  const onSearchInputChange = (updatedSearchValue) => {
    handleUpdateSearchUrl(updatedSearchValue);
    setSearchValue(updatedSearchValue);
  };

  const onClearSearchClick = () => {
    updateSearchUrlImmediately('');
    setSearchValue('');
  };

  const onSearchInputFocus = () => {
    if (filters.length > 0) setShouldShowFilters(true);
  };

  const onFilterClick = ({ isSingleSelect, isToggle, key, value }) => {
    const params = isSingleSelect
      ? urlSearch.setValue(key, isToggle ? true : value, { shouldNavigate: false })
      : urlSearch.appendValue(key, value, { shouldNavigate: false });

    params.delete('page');
    urlSearch.applyParams(params);
  };

  const onClearFiltersClick = () => {
    const params = urlSearch.getParams(search);
    filterParams.forEach((param) => params.delete(param));
    params.delete('page');
    params.delete('filter_token');
    urlSearch.applyParams(params);
  };

  return (
    <div className={styles.element}>
      <CategoryNavigator
        className={className}
        filters={filters}
        hasCustomFilterButton={hasCustomFilterButton}
        isCustomFilterButtonDisabled={isChatOpen}
        isDisabled={isDisabled}
        onCategoryNavigatorBlur={onCategoryNavigatorBlur}
        onClearFiltersClick={onClearFiltersClick}
        onClearSearchClick={onClearSearchClick}
        onCustomFilterClick={onCustomFilterClick}
        onFilterClick={onFilterClick}
        onSearchInputChange={onSearchInputChange}
        onSearchInputFocus={onSearchInputFocus}
        onSelectChange={onSelectChange}
        searchAriaLabel={searchAriaLabel}
        searchPlaceholder={searchPlaceholder}
        searchValue={searchValue}
        selectedFilterValues={selectedFilterValues}
        selectOptions={selectOptions}
        selectPlaceholder={selectPlaceholder}
        selectValue={selectValue}
        shouldShowClearFilters={hasActiveFilters()}
        shouldShowFilters={shouldShowFilters && !customFilterText}
        shouldShowSelect={shouldShowSelect}
      />
      <CustomFilterNotice
        isVisible={Boolean(customFilterText)}
        onEditClick={onEditCustomFilterClick}
        onClearClick={onClearFiltersClick}
        text={customFilterText}
      />
    </div>
  );
};

const optionPropType = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  label: PropTypes.string.isRequired,
});

FilterableSearch.propTypes = {
  className: PropTypes.string,
  customFilterText: PropTypes.string,
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      field_type: PropTypes.string,
      label: PropTypes.string,
      key: PropTypes.string.isRequired,
      allow_multiple_selects: PropTypes.bool,
      options: PropTypes.arrayOf(optionPropType),
    }),
  ),
  hasCustomFilterButton: PropTypes.bool,
  isChatOpen: PropTypes.bool,
  isDisabled: PropTypes.bool,
  onCustomFilterClick: PropTypes.func,
  onEditCustomFilterClick: PropTypes.func,
  onSelectChange: PropTypes.func,
  searchAriaLabel: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  searchQueryParam: PropTypes.string,
  shouldShowSelect: PropTypes.bool,
  selectOptions: PropTypes.arrayOf(optionPropType),
  selectPlaceholder: PropTypes.string,
  selectValue: optionPropType,
};

export default FilterableSearch;
